// Types, validation, duplicate detection, and constants for the Import Voters module.
//
// Every RULE lives in `src/app/voters/engine/voter-rules.ts` — this file only adapts records to it.
// Do not add a validation here; add it there, where the Add voter form and the template read it too.

import { describeReasonCode } from '../../voters/engine/voter-errors';
import {
	fieldsFor,
	normalizeVin,
	toVoterPayload,
	validateVoterInput,
	VOTER_FIELD_RULES,
	type VoterRulesMode,
} from '../../voters/engine/voter-rules';

export type VoterRecordStatus = 'pending' | 'valid' | 'invalid' | 'duplicate' | 'processing' | 'success' | 'failed';

/** Where a sent row ended up. `already` = the register had it; `fix` = rejected, with a reason. */
export type VoterRecordOutcome = 'added' | 'already' | 'fix';

export interface IVoterRecord {
	_rowId: string;
	_status: VoterRecordStatus;
	_errors: string[];
	/** Reason codes, parallel to `_errors` for client-side issues; the server's code after a send. */
	_reasonCodes: string[];
	_selected: boolean;
	_apiError?: string;
	_outcome?: VoterRecordOutcome;
	_phoneNormalized: boolean;
	_phoneWarning: string | null;
	/** 1-based row number in the parsed file (the header is row 1). Stable across edits. */
	_rowNumber: number;
	/** The row's original cells, in file column order — what "rows to fix" and the batch import send back. */
	_cells: string[];
	// API fields
	voterRegistrationNumber: string;
	firstName: string;
	lastName: string;
	middleName: string;
	pollingUnitCode: string;
	phoneNumber: string;
	email: string;
	gender: string;
	dateOfBirth: string;
	// Display-only fields
	stateName: string;
	lgaName: string;
	wardName: string;
	pollingUnitName: string;
}

export interface IColumnMapping {
	voterRegistrationNumber: string;
	firstName: string;
	middleName: string;
	lastName: string;
	pollingUnitCode: string;
	phoneNumber: string;
	email: string;
	gender: string;
	dateOfBirth: string;
	stateName: string;
	lgaName: string;
	wardName: string;
	pollingUnitName: string;
}

export interface IProcessingResult {
	rowId: string;
	rowNumber: number;
	vin: string;
	name: string;
	success: boolean;
	outcome: VoterRecordOutcome;
	reasonCode?: string;
	error?: string;
}

export interface IProcessingState {
	isProcessing: boolean;
	isPaused: boolean;
	total: number;
	processed: number;
	passed: number;
	failed: number;
	results: IProcessingResult[];
	/** Sent and created. */
	added: number;
	/** Sent and found already registered. */
	alreadyRegistered: number;
	/** Sent and rejected by the server. */
	rejected: number;
	/** Rows the review held back (invalid / duplicate in file) — they still count as "to fix". */
	heldBack: number;
	mode: 'batch' | 'legacy' | null;
	/** "Import #n" once the server has numbered the batch. */
	batchNumber: number | null;
	batchId: string | null;
	/** Why the loop is waiting, for the status line. */
	waiting: 'rate-limited' | 'retrying' | 'signed-out' | null;
	/** The import stopped on an error it could not retry past. */
	fatalError: string | null;
	wasAborted: boolean;
	/** Earlier imports of the same file, by content hash — "this file was imported before". */
	sameFileNumbers: number[];
	/** Reading back which rows failed, after the last chunk. */
	reconciling: boolean;
	/** Something the user should know that is not a failure of the import itself. */
	notice: string | null;
}

export type ImportPhase = 'upload' | 'review' | 'processing';

export type FilterStatus = 'all' | 'valid' | 'invalid' | 'duplicate';

export type SortDirection = 'asc' | 'desc';

export interface ISortConfig {
	field: keyof IVoterRecord | null;
	direction: SortDirection;
}

// Editable fields on the review table
export const EDITABLE_FIELDS: (keyof IVoterRecord)[] = [
	'voterRegistrationNumber',
	'firstName',
	'lastName',
	'phoneNumber',
	'pollingUnitCode',
	'email',
	'gender',
	'dateOfBirth',
];

// Column display config for the review table — eight columns, matching the grid in style.scss.
export const COLUMN_CONFIG = [
	{ key: 'firstName' as const, label: 'First Name', mono: false },
	{ key: 'lastName' as const, label: 'Last Name', mono: false },
	{ key: 'phoneNumber' as const, label: 'Phone', mono: true },
	{ key: 'pollingUnitCode' as const, label: 'PU Code', mono: true },
	{ key: 'voterRegistrationNumber' as const, label: 'VIN', mono: true },
	{ key: 'email' as const, label: 'Email', mono: false },
	{ key: 'gender' as const, label: 'Gender', mono: false },
	{ key: 'dateOfBirth' as const, label: 'Date of birth', mono: true },
];

/** Columns that never reach the server — kept so a file's place names show in the review. */
const ENRICHMENT_FIELDS: { key: keyof IColumnMapping; label: string; synonyms: string[] }[] = [
	{ key: 'stateName', label: 'State name (reference only)', synonyms: ['sname', 'state_name', 'statename', 'state'] },
	{ key: 'lgaName', label: 'LGA name (reference only)', synonyms: ['lname', 'lga_name', 'lganame', 'lga'] },
	{ key: 'wardName', label: 'Ward name (reference only)', synonyms: ['rname', 'ward_name', 'wardname', 'ward'] },
	{
		key: 'pollingUnitName',
		label: 'Polling unit name (reference only)',
		synonyms: ['pname', 'polling_unit_name', 'pollingunitname'],
	},
];

export interface ITargetField {
	key: keyof IColumnMapping;
	label: string;
	required: boolean;
}

/** Target fields for column mapping, in mapper order, for the backend in use. */
export const targetFieldsFor = (mode: VoterRulesMode): ITargetField[] => [
	...fieldsFor(mode).map((rule) => ({
		key: rule.field as keyof IColumnMapping,
		label: rule.label,
		required: rule.required[mode],
	})),
	...ENRICHMENT_FIELDS.map((f) => ({ key: f.key, label: f.label, required: false })),
];

/** The strictest (baseline) set — for callers that do not know the backend. */
export const TARGET_FIELDS: ITargetField[] = targetFieldsFor('baseline');

// Auto-detection map: lowercase header → target field. Built from the rules' synonyms.
export const DEFAULT_HEADER_MAP: Record<string, keyof IColumnMapping> = (() => {
	const map: Record<string, keyof IColumnMapping> = {};
	VOTER_FIELD_RULES.forEach((rule) => {
		map[rule.field.toLowerCase()] = rule.field as keyof IColumnMapping;
		rule.synonyms.forEach((s) => {
			map[s] = rule.field as keyof IColumnMapping;
		});
	});
	ENRICHMENT_FIELDS.forEach((f) => {
		map[f.key.toLowerCase()] = f.key;
		f.synonyms.forEach((s) => {
			if (!map[s]) map[s] = f.key;
		});
	});
	return map;
})();

export const EMPTY_COLUMN_MAPPING: IColumnMapping = {
	voterRegistrationNumber: '',
	firstName: '',
	middleName: '',
	lastName: '',
	pollingUnitCode: '',
	phoneNumber: '',
	email: '',
	gender: '',
	dateOfBirth: '',
	stateName: '',
	lgaName: '',
	wardName: '',
	pollingUnitName: '',
};

let rowIdCounter = 0;
export const generateRowId = (): string => `row-${Date.now()}-${(rowIdCounter += 1)}`;

export const normalizeNigerianPhone = (raw: string): { value: string; wasNormalized: boolean } => {
	if (!raw?.trim()) return { value: '', wasNormalized: false };

	// Strip non-digit characters
	const digits = raw.replace(/\D/g, '');

	// Already 11 digits starting with 0 — correct format
	if (digits.length === 11 && digits.startsWith('0')) {
		return { value: digits, wasNormalized: digits !== raw.trim() };
	}

	// 10 digits — missing leading 0
	if (digits.length === 10 && !digits.startsWith('0')) {
		return { value: `0${digits}`, wasNormalized: true };
	}

	// 13 digits starting with 234 — international format
	if (digits.length === 13 && digits.startsWith('234')) {
		return { value: `0${digits.slice(3)}`, wasNormalized: true };
	}

	// Non-conforming — return digits as-is (or stripped version if had non-digits)
	return { value: digits || raw.trim(), wasNormalized: false };
};

export const validatePhoneFormat = (phone: string): string | null => {
	if (!phone?.trim()) return null; // emptiness is the rules' call, not a format warning

	const digits = phone.replace(/\D/g, '');
	if (digits.length === 11 && digits.startsWith('0')) return null; // valid

	if (digits.length < 7) return `Phone too short (${digits.length} digits)`;
	if (digits.length > 15) return `Phone too long (${digits.length} digits)`;
	return `Non-standard phone format (${digits.length} digits)`;
};

/**
 * Map headers to fields. Matching ignores case, surrounding space, and the difference between
 * spaces, dashes and underscores — "Date of Birth", "date_of_birth" and "DATE-OF-BIRTH" all land on
 * `dateOfBirth`.
 */
export const autoDetectMapping = (headers: string[]): IColumnMapping => {
	const mapping: IColumnMapping = { ...EMPTY_COLUMN_MAPPING };
	const usedHeaders = new Set<string>();
	const lookup = (header: string): keyof IColumnMapping | undefined => {
		const lower = header.toLowerCase().trim();
		return (
			DEFAULT_HEADER_MAP[lower] ??
			DEFAULT_HEADER_MAP[lower.replace(/[\s-]+/g, '_')] ??
			DEFAULT_HEADER_MAP[lower.replace(/[\s_-]+/g, ' ')] ??
			DEFAULT_HEADER_MAP[lower.replace(/[\s_-]+/g, '')]
		);
	};

	for (const header of headers) {
		const targetField = lookup(header);
		if (targetField && !mapping[targetField] && !usedHeaders.has(header)) {
			mapping[targetField] = header;
			usedHeaders.add(header);
		}
	}

	return mapping;
};

/** Every rule the record breaks, with reason codes. */
export const validateVoterIssues = (record: IVoterRecord, mode: VoterRulesMode = 'baseline') => validateVoterInput(record, mode);

export const validateVoterRecord = (record: IVoterRecord, mode: VoterRulesMode = 'baseline'): string[] =>
	validateVoterIssues(record, mode).map((issue) => issue.message);

// ── In-file duplicates ──────────────────────────────────────────────────────────────────────
//
// A phone or VIN that appears twice in the file. The FIRST occurrence imports; every later one is
// held back as DUPLICATE_IN_FILE, naming the row it repeats. (It used to hold back every copy, so
// a duplicated voter imported zero times.)

export interface IDupEntry {
	rowId: string;
	rowNumber: number;
}

export type DuplicateIndex = Map<string, IDupEntry[]>;

export const dupKeysOf = (record: Pick<IVoterRecord, 'phoneNumber' | 'voterRegistrationNumber'>): string[] => {
	const keys: string[] = [];
	const vin = normalizeVin(record.voterRegistrationNumber);
	if (vin) keys.push(`vin:${vin}`);
	const phone = normalizeNigerianPhone(record.phoneNumber).value;
	if (/^0\d{10}$/.test(phone)) keys.push(`phone:${phone}`);
	return keys;
};

const byRowNumber = (a: IDupEntry, b: IDupEntry) => a.rowNumber - b.rowNumber;

export const buildDuplicateIndex = (records: readonly IVoterRecord[]): DuplicateIndex => {
	const index: DuplicateIndex = new Map();
	for (const record of records) {
		for (const key of dupKeysOf(record)) {
			const list = index.get(key);
			if (list) list.push({ rowId: record._rowId, rowNumber: record._rowNumber });
			else index.set(key, [{ rowId: record._rowId, rowNumber: record._rowNumber }]);
		}
	}
	for (const list of index.values()) if (list.length > 1) list.sort(byRowNumber);
	return index;
};

export const addToDuplicateIndex = (index: DuplicateIndex, record: IVoterRecord) => {
	for (const key of dupKeysOf(record)) {
		const list = index.get(key) ?? [];
		list.push({ rowId: record._rowId, rowNumber: record._rowNumber });
		list.sort(byRowNumber);
		index.set(key, list);
	}
};

export const removeFromDuplicateIndex = (
	index: DuplicateIndex,
	record: Pick<IVoterRecord, '_rowId' | 'phoneNumber' | 'voterRegistrationNumber'>
) => {
	for (const key of dupKeysOf(record)) {
		const list = index.get(key);
		if (!list) continue;
		const next = list.filter((e) => e.rowId !== record._rowId);
		if (next.length) index.set(key, next);
		else index.delete(key);
	}
};

/** The earlier row this record repeats, or `null` when it is the first (or only) of its kind. */
export const duplicateOf = (record: IVoterRecord, index: DuplicateIndex): IDupEntry | null => {
	for (const key of dupKeysOf(record)) {
		const list = index.get(key);
		if (list && list.length > 1 && list[0].rowId !== record._rowId) return list[0];
	}
	return null;
};

/** Re-derive a record's review status. Records already sent keep theirs. */
export const evaluateRecord = (record: IVoterRecord, index: DuplicateIndex, mode: VoterRulesMode): IVoterRecord => {
	if (record._status === 'success' || record._status === 'failed' || record._status === 'processing') return record;

	const issues = validateVoterIssues(record, mode);
	if (issues.length > 0) {
		return {
			...record,
			_errors: issues.map((i) => i.message),
			_reasonCodes: issues.map((i) => i.code),
			_status: 'invalid',
		};
	}
	const first = duplicateOf(record, index);
	if (first) {
		return {
			...record,
			_errors: [describeReasonCode('DUPLICATE_IN_FILE', String(first.rowNumber))],
			_reasonCodes: ['DUPLICATE_IN_FILE'],
			_status: 'duplicate',
		};
	}
	return { ...record, _errors: [], _reasonCodes: [], _status: 'valid' };
};

export const applyValidationAndDuplicates = (records: IVoterRecord[], mode: VoterRulesMode = 'baseline'): IVoterRecord[] => {
	const index = buildDuplicateIndex(records);
	return records.map((record) => evaluateRecord(record, index, mode));
};

export const toApiPayload = (record: IVoterRecord, mode: VoterRulesMode = 'baseline') => toVoterPayload(record, mode);

export const createEmptyRecord = (rowNumber = 0, mode: VoterRulesMode = 'baseline'): IVoterRecord => {
	const blank: IVoterRecord = {
		_rowId: generateRowId(),
		_status: 'pending',
		_errors: [],
		_reasonCodes: [],
		_selected: false,
		_phoneNormalized: false,
		_phoneWarning: null,
		_rowNumber: rowNumber,
		_cells: [],
		voterRegistrationNumber: '',
		firstName: '',
		lastName: '',
		middleName: '',
		pollingUnitCode: '',
		phoneNumber: '',
		email: '',
		gender: '',
		dateOfBirth: '',
		stateName: '',
		lgaName: '',
		wardName: '',
		pollingUnitName: '',
	};
	return evaluateRecord(blank, new Map(), mode);
};

export const getStatusCounts = (records: IVoterRecord[]) => ({
	total: records.length,
	valid: records.filter((r) => r._status === 'valid').length,
	invalid: records.filter((r) => r._status === 'invalid').length,
	duplicate: records.filter((r) => r._status === 'duplicate').length,
	selected: records.filter((r) => r._selected).length,
	phoneNormalized: records.filter((r) => r._phoneNormalized).length,
	/** Rows an import would send: everything not already added or in flight. */
	sendable: records.filter((r) => r._status !== 'success' && r._status !== 'processing').length,
});

/**
 * What the import button says. Legacy imports send valid rows only. A batch import sends every row
 * not yet added, so the server can record the rejects in Import history; the label counts what is
 * actually sent and names how many of those are valid.
 */
export const importButtonLabel = (counts: ReturnType<typeof getStatusCounts>, sendsEveryRow: boolean) => {
	const rows = (count: number) => `${count.toLocaleString()} row${count === 1 ? '' : 's'}`;
	if (!sendsEveryRow) return `Import ${rows(counts.valid)}`.replace(/ row/, ' valid row');
	if (counts.sendable === counts.valid) return `Import ${rows(counts.valid)}`;
	return `Import ${rows(counts.sendable)} (${counts.valid.toLocaleString()} valid)`;
};

/**
 * The record's cells as the server should see them: the original row, with every MAPPED column
 * overwritten by the record's current value — so an edit made in the review is what gets sent, and
 * a row added by hand (no original cells) is built from the mapping alone.
 */
export const cellsForRecord = (record: IVoterRecord, headers: readonly string[], mapping: IColumnMapping): string[] => {
	const cells = Array.from({ length: headers.length }, (_, i) => String(record._cells[i] ?? ''));
	(Object.keys(mapping) as (keyof IColumnMapping)[]).forEach((field) => {
		const header = mapping[field];
		if (!header) return;
		const index = headers.indexOf(header);
		if (index === -1) return;
		cells[index] = String(record[field] ?? '');
	});
	return cells;
};

/** field → column index, for the batch API. Unmapped fields are omitted. */
export const mappingToIndexes = (headers: readonly string[], mapping: IColumnMapping): Record<string, number> => {
	const out: Record<string, number> = {};
	(Object.keys(mapping) as (keyof IColumnMapping)[]).forEach((field) => {
		const index = mapping[field] ? headers.indexOf(mapping[field]) : -1;
		if (index !== -1) out[field] = index;
	});
	return out;
};
